import React, { useMemo, useState } from 'react';
import Reveal from '../../../shared/Reveal/Reveal';
import { submitPartnershipInquiry } from '../../../../api/partnershipSubmissionsApi';
import { countries, phoneCodeOptions } from '../../../../data/countries';
import { validatePartnershipForm } from '../../../../utils/validatePartnershipForm';
import './Form.css';

const FieldError = ({ message }) =>
  message ? <p className="partnership-form__field-error">{message}</p> : null;

const Form = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [socialMedia, setSocialMedia] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const countrySelectOptions = useMemo(
    () => countries.map(({ code, name }) => ({ value: code, label: name })),
    []
  );

  const inputClass = (field) =>
    `partnership-form__input${fieldErrors[field] ? ' partnership-form__input--error' : ''}`;

  const selectClass = (field) =>
    `partnership-form__select${fieldErrors[field] ? ' partnership-form__input--error' : ''}`;

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const getFormValues = () => ({
    firstName,
    lastName,
    email,
    countryCode,
    phone,
    city,
    country,
    website,
    socialMedia,
    message,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    const values = getFormValues();
    const validation = validatePartnershipForm(values);

    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setFormError('Please fix the highlighted fields and try again.');
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      await submitPartnershipInquiry({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        countryCode: values.countryCode.trim(),
        phone: values.phone.trim(),
        city: values.city.trim(),
        country: values.country.trim(),
        website: values.website.trim(),
        socialMedia: values.socialMedia.trim(),
        message: values.message.trim(),
      });

      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.errors?.length) {
        setFormError(error.errors.join(' '));
      } else {
        setFormError(error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="partnership-form" id="partnership-inquiry">
        <div className="partnership-form__inner">
          <Reveal className="partnership-form__intro">
            <p className="partnership-form__eyebrow">Partnership Inquiry</p>
            <h2 className="partnership-form__title">Inquiry received</h2>
            <p className="partnership-form__text">
              Thank you for your interest in partnering with VBS. Our team will review your
              inquiry and follow up with the most suitable approach.
            </p>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section className="partnership-form" id="partnership-inquiry">
      <div className="partnership-form__inner">
        <Reveal className="partnership-form__intro">
          <p className="partnership-form__eyebrow">Partnership Inquiry</p>
          <h2 className="partnership-form__title">
            Let&apos;s explore a partnership.
          </h2>
          <p className="partnership-form__text">
            Tell us about your business and goals. Our team will review your
            inquiry and follow up with the most suitable partnership approach.
          </p>
        </Reveal>

        <Reveal className="partnership-form__panel" delay={80}>
          <form
            className="partnership-form__form"
            onSubmit={handleSubmit}
            noValidate
          >
            {formError ? (
              <p className="partnership-form__form-error" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="partnership-form__row partnership-form__row--split">
              <div className="partnership-form__field">
                <label className="partnership-form__label" htmlFor="first-name">
                  First name
                </label>
                <input
                  id="first-name"
                  className={inputClass('firstName')}
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    clearFieldError('firstName');
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
                <FieldError message={fieldErrors.firstName} />
              </div>

              <div className="partnership-form__field">
                <label className="partnership-form__label" htmlFor="last-name">
                  Last name
                </label>
                <input
                  id="last-name"
                  className={inputClass('lastName')}
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    clearFieldError('lastName');
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
                <FieldError message={fieldErrors.lastName} />
              </div>
            </div>

            <div className="partnership-form__field">
              <label className="partnership-form__label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className={inputClass('email')}
                type="email"
                name="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  clearFieldError('email');
                }}
                placeholder="you@company.com"
                autoComplete="email"
              />
              <FieldError message={fieldErrors.email} />
            </div>

            <div className="partnership-form__field">
              <label className="partnership-form__label" htmlFor="phone">
                Phone number
              </label>
              <div className="partnership-form__phone">
                <select
                  className={selectClass('countryCode')}
                  name="countryCode"
                  value={countryCode}
                  onChange={(event) => {
                    setCountryCode(event.target.value);
                    clearFieldError('countryCode');
                  }}
                  aria-label="Country code"
                >
                  {phoneCodeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
                <input
                  id="phone"
                  className={`${inputClass('phone')} partnership-form__input--phone`}
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    clearFieldError('phone');
                  }}
                  placeholder="+1 (555) 000-0000"
                  autoComplete="tel"
                />
              </div>
              <FieldError message={fieldErrors.countryCode || fieldErrors.phone} />
            </div>

            <div className="partnership-form__row partnership-form__row--split">
              <div className="partnership-form__field">
                <label className="partnership-form__label" htmlFor="city">
                  City
                </label>
                <input
                  id="city"
                  className={inputClass('city')}
                  type="text"
                  name="city"
                  value={city}
                  onChange={(event) => {
                    setCity(event.target.value);
                    clearFieldError('city');
                  }}
                  placeholder="Your city"
                  autoComplete="address-level2"
                />
                <FieldError message={fieldErrors.city} />
              </div>

              <div className="partnership-form__field">
                <label className="partnership-form__label" htmlFor="country">
                  Country
                </label>
                <select
                  id="country"
                  className={`${selectClass('country')} partnership-form__select--full`}
                  name="country"
                  value={country}
                  onChange={(event) => {
                    setCountry(event.target.value);
                    clearFieldError('country');
                  }}
                >
                  <option value="" disabled>
                    Select country
                  </option>
                  {countrySelectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FieldError message={fieldErrors.country} />
              </div>
            </div>

            <div className="partnership-form__field">
              <label className="partnership-form__label" htmlFor="website">
                Website / URL
              </label>
              <input
                id="website"
                className={inputClass('website')}
                type="url"
                name="website"
                value={website}
                onChange={(event) => {
                  setWebsite(event.target.value);
                  clearFieldError('website');
                }}
                placeholder="Paste your URL"
              />
              <FieldError message={fieldErrors.website} />
            </div>

            <div className="partnership-form__field">
              <label className="partnership-form__label" htmlFor="social">
                Social media
              </label>
              <input
                id="social"
                className={inputClass('socialMedia')}
                type="url"
                name="socialMedia"
                value={socialMedia}
                onChange={(event) => {
                  setSocialMedia(event.target.value);
                  clearFieldError('socialMedia');
                }}
                placeholder="Paste your link"
              />
              <FieldError message={fieldErrors.socialMedia} />
            </div>

            <div className="partnership-form__field">
              <label className="partnership-form__label" htmlFor="message">
                Message
              </label>
              <textarea
                id="message"
                className={`partnership-form__textarea${
                  fieldErrors.message ? ' partnership-form__input--error' : ''
                }`}
                name="message"
                rows={5}
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  clearFieldError('message');
                }}
                placeholder="Briefly describe your audience, region, and what success looks like for you."
              />
              <FieldError message={fieldErrors.message} />
            </div>

            <button
              type="submit"
              className="partnership-form__submit luxury-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Submit Inquiry'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Form;
